<template>
  <div>
    <paginate
      v-if="reversePagination === false"
      :options="paginateOptions"
      :show-options="showOptions && !selector"
      :paginate="pagination"
      @change="paginate"
      :bulk-actions="bulkActions"
      :selected="Array.isArray(selected) ? selected : []"
      :isSmall="isSmall"
    />
    <table class="table is-fullwidth" :class="{ 'is-narrow': isSmall }">
      <thead>
        <tr>
          <th
            class="index-column sortable"
            @click="sortBy({ sortBy: identifier, sortable: true })"
          >
            #
          </th>
          <th class="index-column"></th>
          <th
            v-for="(column, idx) in internalColumns"
            :key="idx"
            @click="sortBy(column)"
            :class="{
              sortable: column.sortable,
              'has-text-right': column.right,
              'small-column-text': isSmall,
            }"
          >
            {{ column.title }}
            <i
              v-if="
                pagination.sort.column === column.id ||
                pagination.sort.column === column.sortBy
              "
              class="fa"
              :class="{
                'fa-sort-down': pagination.sort.direction === 'ASC',
                'fa-sort-up': pagination.sort.direction === 'DESC',
              }"
            ></i>
            <span class="help" v-if="column.help" :title="column.help">?</span>
          </th>
          <th class="action-column"></th>
        </tr>
        <!-- Display bar only if necessary: hasFilters, enableDownload, hasSelection -->
        <tr
          v-if="enableDownload || hasFilters || (!singleSelect && !noSelection)"
          :class="{ 'has-background-yellow': isFiltered && objectName !== '' }"
        >
          <td>
            <span
              v-if="!singleSelect && enableDownload"
              @click.stop="$refs.download.click()"
            >
              <i class="fa fa-cloud-download"></i>
            </span>
            <div v-if="downloads.length > 0" class="dropdown is-hoverable">
              <div class="dropdown-trigger">
                <span class="icon">
                  <i class="fa fa-cloud-download"></i>
                </span>
              </div>
              <div class="dropdown-menu">
                <div class="dropdown-content">
                  <a
                    v-for="(option, index) in downloads"
                    :key="index"
                    class="dropdown-item"
                    @click="handleDownload(option)"
                  >
                    <span class="icon">
                      <i :class="option.icon || 'fa fa-download'"></i>
                    </span>
                    <span>{{ option.label }}</span>
                  </a>
                </div>
              </div>
            </div>
          </td>
          <td>
            <input
              v-if="!singleSelect && !noSelection"
              type="checkbox"
              v-model="selectAll"
              :cy-data="selectAllCyData || undefined"
            />
          </td>
          <td
            v-for="(column, idx) in internalColumns"
            :key="idx"
            :cy-data="column.id"
            class="has-text-centered"
          >
            <div v-if="column.hasFilter" class="filter-container">
              <div class="field has-addons">
                <p class="control">
                  <input
                    v-if="
                      internalColumns[idx].filterType === '' &&
                      column.hasTextFilter
                    "
                    class="filter-text"
                    :type="column.inputType"
                    :name="column.id"
                    v-model.trim="internalColumns[idx].filter"
                    @input="updateFilter"
                  />
                  <input
                    v-if="internalColumns[idx].filterType !== ''"
                    class="filter-text"
                    :value="getFilterPlaceholder(idx, column)"
                    disabled
                  />
                </p>
                <div class="control" v-if="column.advancedFilter">
                  <div class="dropdown is-hoverable">
                    <div class="dropdown-trigger">
                      <button
                        class="button"
                        aria-haspopup="true"
                        aria-controls="dropdown-menu"
                      >
                        <span class="icon is-small">
                          <i class="fa fa-filter"></i>
                        </span>
                      </button>
                    </div>
                    <div class="dropdown-menu" id="dropdown-menu" role="menu">
                      <div class="dropdown-content">
                        <template v-for="(option, optionIdx) in column.advancedFilterOptions">
                          <a :key="'filter_option_' + optionIdx" class="dropdown-item" @click="setFilterType(idx, option.value)">
                            {{ option.label }}
                          </a>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <checkbox-field
              :label="''"
              v-if="column.switcher"
              :checked="allSwitched(column.id)"
              @change="switchAll(column.id)"
              :inline="false"
            />
          </td>
          <td class="action-column"></td>
        </tr>
      </thead>
      <tbody>
        <tr v-if="loading">
          <td :colspan="internalColumns.length + 3" class="loader-container">
            <i class="loader"></i>
          </td>
        </tr>
        <tr v-if="tableSummary !== null" class="has-background-info-light">
          <td colspan="2">Totaux</td>
          <td
            v-for="(column, idx) in internalColumns"
            :key="'summary_' + idx"
            :cy-data="'summary_' + column.id"
            class="has-text-right"
          >
            <slot :name="'summary_' + column.id">
              <template v-if="tableSummary[column.id]">
                {{ tableSummary[column.id] | priceEUR }}
              </template>
            </slot>
          </td>
          <td></td>
        </tr>
        <tr
          v-for="(item, index) in items"
          :key="index"
          :class="{
            selected:
              selected &&
              (selected[identifier] === item[identifier] ||
                (!singleSelect &&
                  selected.findIndex(
                    (e) => e[identifier] === item[identifier]
                  ) !== -1)),
            selectable: selector || singleSelect,
          }"
          @click="
            !noSelection && (selector || singleSelect) && toggleSelect(item)
          "
        >
          <td>#{{ (pagination.page - 1) * pagination.size + index + 1 }}</td>
          <td>
            <input
              v-if="!singleSelect && !noSelection && item[identifier]"
              type="checkbox"
              @click.stop
              @input="toggleSelect(item)"
              :checked="
                selected &&
                selected.findIndex(
                  (e) => e[identifier] === item[identifier]
                ) !== -1
              "
            />
            <i
              v-if="selected && selected[identifier] === item[identifier]"
              class="fa fa-check"
            ></i>
          </td>
          <td
            v-for="(column, idx) in internalColumns"
            :key="idx"
            :cy-data="column.id"
          >
            <slot :name="column.id" :item="item">
              <template v-if="!column.component">
                {{ displayItem(item, column.id) }}
              </template>
              <template v-else>
                <component
                  :is="column.component"
                  :value="displayItem(item, column.id)"
                />
              </template>
            </slot>
          </td>
          <td class="action-column">
            <slot name="actions" :item="item" :index="index"></slot>
          </td>
        </tr>
        <tr v-if="!loading && items.length == 0">
          <td :colspan="internalColumns.length + 3" class="no-data">
            <div>{{ emptyTableMessage }}</div>
          </td>
        </tr>
      </tbody>
    </table>
    <form
      v-if="enableDownload"
      :style="{ display: 'none' }"
      target="_blank"
      :action="fullCsvPath"
      method="POST"
    >
      <button type="submit" ref="download"></button>
      <input type="hidden" name="token" :value="authToken" />
    </form>
    <paginate
      v-if="reversePagination"
      :options="paginateOptions"
      :show-options="showOptions && !selector"
      :paginate="pagination"
      @change="paginate"
      :bulk-actions="bulkActions"
      :selected="Array.isArray(selected) ? selected : []"
      :isSmall="isSmall"
    />
  </div>
</template>

<script>
import axios from "axios";
import { debounce } from "lodash";
import { mapGetters } from "vuex";
import PaginationBuilder from "../lib/Paginate";
import Paginate from "./Paginate";

export default {
  name: "datatable",
  components: {
    Paginate,
  },
  props: {
    fetch: Function,
    singleSelect: {
      type: Boolean,
      default: false,
    },
    selector: {
      type: Boolean,
      default: false,
    },
    noSelection: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [Object, Array],
      default: () => null,
    },
    columns: {
      type: Object,
      default: () => null,
    },
    enableDownload: {
      type: Boolean,
      default: false,
    },
    /**
     * Options of download button. This will display a dropdown with the options
     * The baseUrl will be the base url of the download.
     * The params will be the params of the download.
     * The label will be the label of the download.
     * The icon will be the icon of the download.
     * @example
     * [
     *  {
     *    label: "Download as CSV",
     *    baseUrl: "/api/v1/download",
     *    params: {
     *      param1: "value1",
     *      param2: "value2",
     *    }
     *  }
     * ]
     */
    downloads: {
      type: Array,
      default: () => [],
    },
    objectName: {
      type: String,
      default: "",
    },
    size: {
      type: Number,
      default: 50,
    },
    filterColumns: {
      type: Boolean,
      default: false,
    },
    identifier: {
      type: String,
      default: "id",
    },
    customEmptyTableMessage: {
      type: String,
      default: "",
    },
    order: {
      type: String,
      default: "ASC",
    },
    externalFilters: {
      type: Object,
      default: () => null,
    },
    paginateOptions: {
      type: Array,
      default: () => [50, 100, 200],
    },
    showOptions: {
      type: Boolean,
      default: () => true,
    },
    bulkActions: {
      type: Array,
    },
    additionalDownloadParams: {
      type: String,
      default: "",
    },
    isSmall: {
      type: Boolean,
      default: false,
    },
    /** Stable Cypress hook for the header select-all checkbox; set per datatable when several appear on one page. */
    selectAllCyData: {
      type: String,
      default: null,
    },
    reversePagination: {
      type: Boolean,
      default: false,
    },
    csvPath: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loading: false,
      internalColumns: [],
      hasFilters: false,
      items: [],
      pagination: {
        page: 1,
        total: 0,
        size: this.size,
        sort: {
          column: this.identifier,
          direction: this.order,
        },
      },
      selectAll: false,
      selected: [],
      API: axios.defaults.baseURL,
      authToken: this.$store.getters["auth/getToken"],
      emptyTableMessage: this.customEmptyTableMessage,
      tableSummary: null,
    };
  },
  mounted() {
    this.init();
  },
  watch: {
    value() {
      this.selected = this.value;
    },
    selectAll() {
      this.selected = [];
      if (this.selectAll === true) {
        this.items.forEach((item) => {
          this.selected.push(item);
        });
      }
      this.$emit("input", this.selected);
    },
    externalFilters(newFilters, oldFilters) {
      if (JSON.stringify(oldFilters) === JSON.stringify(newFilters)) return;
      this.refresh();
    },
  },
  computed: {
    ...mapGetters({
      getFilters: "filters/getFilters",
    }),
    selectedToUrl() {
      return this.selected
        ? this.singleSelect
          ? this.selected.id
          : this.selected.map((object) => object.id).toString()
        : "";
    },
    isFiltered() {
      let empty = true;
      this.internalColumns.forEach((column) => {
        if (column.filter !== "") {
          empty = false;
        }
      });
      return !empty;
    },
    fullCsvPath() {
      return (
        this.API +
        (this.csvPath ||
          `/${this.objectName}/csv?${this.getParams()}&selected=${
            this.selectedToUrl
          }`)
      );
    },
  },
  methods: {
    init() {
      this.selected = this.value;

      /* Columns are automatically determined by the children. */
      if (!this.columns) {
        Object.keys(this.$scopedSlots).forEach((key) => {
          const slot = this.$scopedSlots[key];
          if (!slot({ item: "test" })) return;
          const slotBlockData = slot({ item: "test" })[0].data;
          if (!slotBlockData.attrs) {
            return;
          }
          const t = slotBlockData.attrs.title;
          const nofilter = slotBlockData.attrs.nofilter != null;
          const switcher = slotBlockData.attrs.switcher != null;
          const advancedFilter = slotBlockData.attrs.advancedFilter != null;
          let advancedFilterOptions = [
            {
              label: 'Normal',
              value: '',
            },
            {
              label: 'Vide',
              value: 'null',
            },
            {
              label: 'Non vide',
              value: 'notnull',
            }
          ];
          advancedFilterOptions = slotBlockData.attrs.advancedFilterOptions || advancedFilterOptions;
          if (!nofilter || advancedFilter) {
            this.hasFilters = true;
          }

          const hasTextFilter = !nofilter;

          this.internalColumns.push({
            id: key,
            fn: slot,
            title: t,
            hasFilter: !nofilter || advancedFilter,
            switcher: switcher,
            filter: "",
            hasTextFilter,
            filterType: "",
            advancedFilter,
            advancedFilterOptions,
            inputType: slotBlockData.attrs.type
              ? slotBlockData.attrs.type
              : "text",
            sortable: slotBlockData.attrs.sortable || false,
            sortBy: slotBlockData.attrs.sortBy || key,
            help: slotBlockData.attrs.help ? slotBlockData.attrs.help : "",
            right: slotBlockData.attrs.right,
          });
        });
      } else {
        Object.keys(this.columns).forEach((id) => {
          let title = "";
          let filter = "";
          let hasFilter = this.filterColumns;
          let hasTextFilter = false;
          let advancedFilter = false;
          let advancedFilterOptions = [
            {
              label: 'Normal',
              value: '',
            },
            {
              label: 'Vide',
              value: 'null',
            },
            {
              label: 'Non vide',
              value: 'notnull',
            }
          ];

          /**
           * If the column is only defined as a key-value set like: key => label
           */
          if (typeof this.columns[id] === "string") {
            title = this.columns[id];
            if(this.filterColumns) {
              filter = '';
              hasFilter = true;
              hasTextFilter = true;
            }
          }
          /**
           * If the column is defined as a key-value set like: key => { title: "label", filter: "value", advancedFilter: true, sortable: true, sortBy: "key", component: "component" }
           */
          else {
            const column = this.columns[id];
            title = column.title;
            filter = column.filter;
            hasTextFilter = column.filter !== undefined;
            advancedFilter = column.advancedFilter || false;
            hasFilter = hasTextFilter || column.advancedFilter !== false;
            advancedFilterOptions = column.advancedFilterOptions || advancedFilterOptions;
          }

          if (hasFilter) {
            this.hasFilters = true;
          }

          this.internalColumns.push({
            id,
            title,
            filter,
            hasTextFilter,
            hasFilter,
            filterType: "",
            advancedFilter,
            advancedFilterOptions,
            inputType: this.columns[id].inputType || "text",
            sortable: this.columns[id].sortable || false,
            sortBy: this.columns[id].sortBy || this.key,
            component: this.columns[id].component || null,
          });
        });
      }
      const filters = this.getFilters(this.objectName);
      if (filters) {
        this.internalColumns = filters;
      }
      this.refresh();
    },
    refresh() {
      const p = this.preparePaginationObject();

      this.items = [];
      this.loading = true;
      this.fetch(p)
        .then(this.callback)
        .catch(this.fetchError)
        .finally(() => {
          this.$nextTick(() => {
            this.loading = false;
          });
        });
    },
    callback(data) {
      this.pagination.total = data.totalElements;
      this.items = Array.isArray(data.content) ? data.content : [];
      this.emptyTableMessage = this.$t("expressions.noData");
      this.tableSummary = data.summary || null;
    },
    fetchError() {
      this.emptyTableMessage = this.$t("expressions.fetchingError");
    },
    preparePaginationObject() {
      return {
        ...this.pagination,
        page: this.pagination.page - 1,
        sort: {
          ...this.pagination.sort,
        },
        filters: this.internalColumns.reduce(
          (filters, column) => {
            const allFilters = filters;
            if (typeof column.filter !== "undefined")
              allFilters[column.id] = column.filter;
            return filters;
          },
          { ...this.externalFilters }
        ),
      };
    },
    getParams() {
      const p = new PaginationBuilder(this.preparePaginationObject());
      return `${p.toString()}&${this.additionalDownloadParams}`;
    },
    paginate(p) {
      this.pagination = p;
      this.refresh();
    },
    displayItem(item, column) {
      const keys = column.split(".");
      let result = item;
      for (let i = 0; i < keys.length; i += 1) {
        const k = keys[i];
        if (!result[k]) {
          return "";
        }
        result = result[k];
      }
      return result;
    },
    toggleSelect(item) {
      if (this.singleSelect) {
        this.selected = item;
        this.$emit("input", this.selected);
        return;
      }

      if (!this.selected) {
        this.selected = [];
      }
      const identifier = this.identifier
      const idx =
        this.selected && this.selected.findIndex((e) => e[identifier] === item[identifier]);
      if (idx === -1) {
        this.selected.push(item);
      } else {
        this.selected.splice(idx, 1);
      }
      this.$emit("input", this.selected);
    },
    updateFilter: debounce(function refresh() {
      this.refresh();
      if (this.objectName !== "") {
        this.$store.dispatch("filters/setFilters", {
          name: this.objectName,
          filters: this.internalColumns,
        });
      }
    }, 500),
    download() {
      this.$emit("download", null);
    },
    sortBy(column) {
      const attribute = column.sortBy || column.id;

      if (!column.sortable) return;

      if (this.pagination.sort.column === attribute) {
        this.pagination.sort.direction =
          this.pagination.sort.direction === "ASC" ? "DESC" : "ASC";
      } else {
        this.pagination.sort.direction = "ASC";
        this.pagination.sort.column = attribute;
      }

      this.refresh();
    },
    switchAll(columnName) {
      let nextState = true;
      if (this.allSwitched(columnName)) {
        nextState = false;
      }

      this.items.forEach((item) => {
        item[columnName] = nextState;
      });
    },
    allSwitched(columnName) {
      return this.items.every((item) => !!item[columnName]);
    },
    handleDownload(option) {
      const form = document.createElement('form');
      form.method = 'POST';
      form.target = '_blank';

      // Concat the API URL with the baseUrl and the params
      form.action = this.API + option.baseUrl + '?' + this.getParams() + '&selected=' + this.selectedToUrl;

      const tokenInput = document.createElement('input');
      tokenInput.type = 'hidden';
      tokenInput.name = 'token';
      tokenInput.value = this.authToken;
      form.appendChild(tokenInput);

      if (option.params) {
        Object.entries(option.params).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          form.appendChild(input);
        });
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },
    getFilterPlaceholder(idx, column) {
      const option = column.advancedFilterOptions.find(
        (opt) => opt.value === column.filterType
      );
      return option ? option.label : "";
    },
    setFilterType(idx, type) {
      const column = this.internalColumns[idx];
      column.filterType = type;
      column.filter = type;
      this.updateFilter();
    },
  },
};
</script>

<style lang="scss" scoped>
$primary: #201E5B;

.index-column {
  width: 20px;
}
.table th {
  text-align: center;
}

tbody {
  font-size: 14px;
}

.action-column {
  width: 50px;
  .button {
    height: 20px;
  }
}

.loader-container {
  font-size: 30px;
  text-align: center;
  background-color: #fff;
  .loader {
    margin: 0 auto;
  }
}

.no-data {
  text-align: center;
  background-color: #fff;
  color: #777;
  padding: 40px 0;
}

.selectable {
  cursor: pointer;
}

.selected {
  background-color: #00EDA6;
  color:#201E5B;
}

.fa-cloud-download {
  color: $primary;
  font-size: 24px;
  cursor: pointer;
}

input[type="text"] {
  width: 100%;
  height: 26px;
  font-size: 12px;
  line-height: 22px;
  padding: 2px 5px;
  margin: 0;
}

input[type="date"] {
  width: 100%;
  height: 26px;
  font-size: 12px;
  line-height: 22px;
  padding: 2px 5px;
  margin: 0;
}

.sortable {
  cursor: pointer;
  color: #154194;
  i {
    margin-left: 5px;
  }
}
.help {
  display: inline-block;
  color: white;
  background-color: #201E5B;
  border-radius: 50%;
  padding: 0 6px;
  cursor: pointer;
}

.small-column-text {
  font-size: 0.85rem;
}

.filter-container {
  width: 100%;
}

.filter-container .field.has-addons {
  justify-content: flex-start;
}

.filter-container .button {
  height: 26px;
  padding: 0 8px;
  border-radius: 2px;
  border-color: #8e8e9c;
}

.filter-container .dropdown-menu {
  min-width: 120px;
}

.filter-text {
  width: 100%;
  height: 26px;
  font-size: 12px;
  line-height: 22px;
  padding: 2px 5px;
  margin: 0;
}

.filter-text:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
  color: #7a7a7a;
}
</style>
